/**
 * InviteAcceptPage — the invitee lands here via /invite/:token,
 * sets their password (with live policy validation), and is auto-logged in.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import PasswordField, { checkPasswordClient } from './PasswordField';
import Logo from '../branding/Logo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InviteAcceptPage() {
  const { token } = useParams();
  const { refresh } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [acceptedTenantPath, setAcceptedTenantPath] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/users/invites/${token}`);
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          if (cancelled) return;
          setLoadError(body.detail || 'This invite link is not valid.');
          setInvite(null);
        } else {
          const data = await res.json();
          if (cancelled) return;
          setInvite(data);
          setName(data.name || '');
        }
      } catch {
        if (!cancelled) setLoadError('Unable to reach QUOD-Finder.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const { ok: passwordOk } = checkPasswordClient(password);
  const confirmOk = password.length > 0 && password === confirm;
  const canSubmit = !submitting && passwordOk && confirmOk && name.trim().length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/users/invites/${token}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, confirm_password: confirm, name }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        let msg = 'Could not complete account setup.';
        if (body.detail) {
          if (typeof body.detail === 'string') msg = body.detail;
          else if (body.detail.messages) msg = body.detail.messages.join(' ');
        }
        setSubmitError(msg);
        return;
      }
      // Iter 38e — Grassboots invite tenancy fix: redirect the new user
      // straight into their tenant landing path (/t/<slug>) if the invite
      // carried tenant context, instead of dumping them on the platform
      // root URL. JWT/user.tenant_id is still authoritative for data
      // access; the URL is purely for clean tenant UX.
      const accepted = await res.json().catch(() => ({}));
      const tenantPath = invite?.tenant_path || accepted?.tenant_path || null;
      setAcceptedTenantPath(tenantPath);
      setDone(true);
      await refresh();
    } catch (err) {
      setSubmitError(err?.message || 'Unable to reach QUOD-Finder.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="auth-shell" data-testid="invite-loading">
        <div className="auth-card"><p className="auth-subtitle">Loading your invite…</p></div>
      </div>
    );
  }

  if (loadError || !invite) {
    return (
      <div className="auth-shell" data-testid="invite-invalid">
        <div className="auth-card">
          <h1 className="auth-title">This invite link can't be used</h1>
          <p className="auth-subtitle" data-testid="invite-invalid-message">
            {loadError || 'Please contact your administrator for a new invite.'}
          </p>
        </div>
        <footer className="auth-footer app-powered-footer"><span className="app-powered-footer-copy">© 2026 QUOD-Tech Ltd. All rights reserved.</span> <span className="app-powered-footer-sep" aria-hidden="true">·</span> <span className="app-powered-footer-mark">POWERED BY QUOD-TECH</span></footer>
      </div>
    );
  }

  return (
    <div className="auth-shell" data-testid="invite-page">
      <div className="auth-card">
        <div className="auth-brand">
          <Logo variant="auth" imgClassName="auth-logo" />
        </div>

        {!done ? (
          <>
            <h1 className="auth-title">Set up your QUOD-Finder account</h1>
            <p className="auth-subtitle">
              You've been invited as <strong>{invite.role_label}</strong>.
            </p>

            <form onSubmit={handleSubmit} className="auth-form" data-testid="invite-form">
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={invite.email}
                  readOnly
                  data-testid="invite-email-readonly"
                  style={{ background: 'var(--bg)', cursor: 'not-allowed' }}
                />
              </div>

              <div className="form-group">
                <label htmlFor="invite-name">Full name</label>
                <input
                  id="invite-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  autoComplete="name"
                  data-testid="invite-name-input"
                />
              </div>

              <PasswordField
                id="invite-password"
                value={password}
                onChange={setPassword}
                label="Create password"
                testIdPrefix="invite-password"
                autoComplete="new-password"
              />

              <div className="form-group">
                <label htmlFor="invite-confirm">Confirm password</label>
                <input
                  id="invite-confirm"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  data-testid="invite-confirm-input"
                />
                {confirm.length > 0 && !confirmOk && (
                  <div
                    className="auth-helper-error"
                    data-testid="invite-confirm-mismatch"
                  >
                    Passwords do not match.
                  </div>
                )}
              </div>

              {submitError && (
                <div className="auth-error" data-testid="invite-error">{submitError}</div>
              )}

              <button
                type="submit"
                className="btn btn-primary"
                disabled={!canSubmit}
                data-testid="invite-submit-button"
                style={{ width: '100%', marginTop: 4 }}
              >
                {submitting ? 'Creating your account…' : 'Activate account'}
              </button>
            </form>
          </>
        ) : (
          <div data-testid="invite-success">
            <h1 className="auth-title">You're all set</h1>
            <p className="auth-subtitle">Welcome to QUOD-Finder, {name}.</p>
            <button
              className="btn btn-primary"
              onClick={() => { window.location.href = acceptedTenantPath || '/'; }}
              data-testid="invite-continue"
              style={{ width: '100%', marginTop: 8 }}
            >
              Continue to QUOD-Finder
            </button>
          </div>
        )}
      </div>

      <footer className="auth-footer">Powered by QUOD-Tech</footer>
    </div>
  );
}
